'use client';

import React, { useRef, useState, useEffect, useContext } from 'react';
import classNames from 'classnames';
import { StockInfoHeader } from '@benzinga/quotes-ui';
import { StockInfoChart } from '@benzinga/quotes-ui';
import { Icon } from '@benzinga/core-ui';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { ArticleFeaturedTicker } from '../entities';
import { useScrollShadow } from '@benzinga/hooks';
import { RankingDetail, QuotesManager } from '@benzinga/quotes-manager';
import { SessionContext } from '@benzinga/session-context';
import styles from './ArticleFeaturedTickersList.module.scss';

interface ArticleFeaturedTickersListProps {
  fillHeight?: boolean;
  tickers: ArticleFeaturedTicker[];
}

export const ArticleFeaturedTickersList: React.FC<ArticleFeaturedTickersListProps> = ({
  fillHeight = false,
  tickers,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const session = useContext(SessionContext);
  const [rankingData, setRankingData] = useState<RankingDetail | null>(null);
  const [isLoadingRankingData, setIsLoadingRankingData] = useState(true);

  useEffect(() => {
    const fetchFreshRankingData = async () => {
      if (!tickers[0]?.symbol) {
        setIsLoadingRankingData(false);
        return;
      }

      try {
        const quotesManager = session.getManager(QuotesManager);
        const response = await quotesManager.getTickerDetails([tickers[0].symbol]);

        if (response?.ok?.result?.[0]) {
          setRankingData(response.ok.result[0].rankings ?? null);
        }
      } catch (error) {
        console.warn('Failed to fetch ranking data:', error);
        setRankingData(null);
      } finally {
        setIsLoadingRankingData(false);
      }
    };

    fetchFreshRankingData();
  }, [tickers, session]);

  const { showEnd } = useScrollShadow(scrollContainerRef, 'vertical');

  const scrollToBottom = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({
        behavior: 'smooth',
        top: container.scrollHeight,
      });
    }
  };

  return (
    <div
      className={classNames(styles.container, 'article-header-ticker-list', {
        [styles.fillHeight]: fillHeight,
        'h-full': fillHeight,
      })}
    >
      <div className={classNames('flex flex-col w-full min-h-0', { 'h-full': fillHeight })}>
        <div className={classNames('article-header-main-ticker', fillHeight && 'min-h-0')} key={tickers[0].symbol}>
          <div className="mb-1">
            <StockInfoChart
              changePercent={tickers[0].changePercent}
              isLoading={isLoadingRankingData}
              isPrimaryTicker
              logoUrl={tickers[0].logoUrl}
              name={tickers[0].name}
              price={tickers[0].price}
              rankingData={rankingData}
              symbol={tickers[0].symbol}
              type={tickers[0].type}
            />
          </div>
        </div>
        <div className="block text-center rounded-b-[8px] bg-[#223E6C] mb-2 py-2 uppercase shrink-0">
          <a className="block font-semibold text-white" href={`/quote/${tickers[0].symbol}`}>
            Overview
          </a>
        </div>
        {tickers.length > 1 && (
          <div className={classNames(styles.secondaryTickers, 'relative flex min-h-0 w-full flex-col')}>
            <div
              className={classNames('scrollable-container w-full overflow-y-auto overflow-x-hidden max-h-[320px]', {
                'has-bottom-fade': showEnd,
              })}
              ref={scrollContainerRef}
            >
              {tickers.map((ticker, index) => {
                if (index === 0) {
                  return null;
                }
                return (
                  <StockInfoHeader
                    changePercent={ticker.changePercent}
                    key={ticker.symbol}
                    logoUrl={ticker.logoUrl}
                    name={ticker.name}
                    price={ticker.price}
                    symbol={ticker.symbol}
                    type={ticker.type}
                  />
                );
              })}
            </div>
            {showEnd && (
              <div className="fade-bottom">
                <Icon className="scroll-arrow" icon={faChevronDown} onClick={scrollToBottom} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

ArticleFeaturedTickersList.displayName = 'ArticleFeaturedTickersList';

export default ArticleFeaturedTickersList;
